import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { supabase } from '../lib/supabase';
import {
  TrendingUp,
  DollarSign,
  Zap,
  Calendar,
  Download,
  Loader2,
  ArrowUpDown,
  AlertCircle,
  FileText,
  Edit3,
  Check,
  X,
  Layers,
  TrendingDown,
  Minus,
  Copy,
  ShoppingCart,
  Grid3x3,
  Eye,
  EyeOff,
  Trash2
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import SalesComparisonChart from './SalesComparisonChart';
import { ProjectionService, ProjectionData } from '../services/projectionService';
import { DuplicateManager } from './DuplicateManager';

interface Project {
  id: string;
  name: string;
  value: number | null;
  contact: string | null;
  plates: number | null;
  sale_year: number | null;
  sale_date: string | null;
  created_at: string;
  panel_power: number | null;
  inverter1_power: number | null;
  inverter1_quantity: number | null;
  inverter2_power: number | null;
  inverter2_quantity: number | null;
}

interface CombinedItem {
  id: string;
  type: 'project' | 'contract';
  name: string;
  value: number;
  date: Date;
  plates: number | null;
  contact: string | null;
  modules?: number;
  modulePower?: number;
  solarPower?: number;
  inverter1Qty?: number;
  inverter1Power?: number;
  inverter2Qty?: number;
  inverter2Power?: number;
  inverterPower?: number;
}

type DataSource = 'combined' | 'projects' | 'contracts';
type SortField = 'name' | 'value' | 'date' | 'plates';
type SortOrder = 'asc' | 'desc';

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  viewMode: 'monthly' | 'quarterly';
  allData: CombinedItem[];
  projectionData?: ProjectionData | null;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label, viewMode, allData, projectionData }) => {
  if (!active || !payload || !payload.length) return null;

  const currentData = payload[0]?.payload;
  if (!currentData) return null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const calculateYoYComparison = () => {
    if (currentData.isProjection) return null;

    let currentValue = 0;
    let previousValue = 0;
    let currentPeriod = '';
    let previousPeriod = '';

    if (viewMode === 'quarterly') {
      const year = currentData.year;
      const quarter = currentData.quarter;
      currentValue = currentData.total;
      currentPeriod = `Q${quarter}/${year}`;
      previousPeriod = `Q${quarter}/${year - 1}`;

      allData.forEach(item => {
        const itemYear = item.date.getFullYear();
        const itemMonth = item.date.getMonth();
        const itemQuarter = Math.floor(itemMonth / 3) + 1;

        if (itemYear === year - 1 && itemQuarter === quarter) {
          previousValue += item.value;
        }
      });
    } else {
      const year = currentData.year;
      const monthNum = currentData.monthNum;
      currentValue = currentData.total;
      const monthDate = new Date(year, monthNum, 1);
      currentPeriod = format(monthDate, 'MMM/yyyy', { locale: ptBR });
      const prevMonthDate = new Date(year - 1, monthNum, 1);
      previousPeriod = format(prevMonthDate, 'MMM/yyyy', { locale: ptBR });

      allData.forEach(item => {
        const itemYear = item.date.getFullYear();
        const itemMonth = item.date.getMonth();

        if (itemYear === year - 1 && itemMonth === monthNum) {
          previousValue += item.value;
        }
      });
    }

    if (previousValue === 0) return null;

    const percentageChange = ((currentValue - previousValue) / previousValue) * 100;
    const isPositive = percentageChange > 0;
    const isNeutral = Math.abs(percentageChange) < 0.01;

    return {
      currentValue,
      previousValue,
      percentageChange,
      isPositive,
      isNeutral,
      currentPeriod,
      previousPeriod
    };
  };

  const yoyData = calculateYoYComparison();
  const currentTotal = currentData.total || 0;
  const projects = currentData.projects || 0;
  const contracts = currentData.contracts || 0;

  return (
    <div className="bg-white p-4 rounded-lg shadow-xl border-2 border-gray-300 min-w-[320px] max-w-[380px] transition-all duration-200">
      <div className="font-bold text-gray-900 mb-3 pb-2 border-b-2 border-gray-200 flex items-center justify-between">
        <span>{currentData.isProjection ? `${label} (Projeção)` : label}</span>
        {currentData.isProjection && (
          <span className="text-xs font-normal bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
            Estimativa
          </span>
        )}
      </div>

      {currentData.isProjection && currentData.projectionDetails ? (
        <div className="space-y-3 animate-fade-in">
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-3 rounded-lg border border-orange-200">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Valor Projetado:</span>
              <span className="font-bold text-lg text-orange-600">
                {formatCurrency(currentData.projectionDetails.projectedValue)}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className={ProjectionService.getConfidenceColor(currentData.projectionDetails.confidence)}>
                {ProjectionService.getConfidenceLabel(currentData.projectionDetails.confidence)}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Dados Históricos ({viewMode === 'quarterly' ? 'Trimestre' : 'Mês'})
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1 custom-scrollbar">
              {currentData.projectionDetails.historicalData.slice(0, 5).map((histData: any, idx: number) => (
                <div
                  key={idx}
                  className="flex justify-between items-center py-1.5 px-2 bg-gray-50 hover:bg-gray-100 rounded transition-colors"
                >
                  <span className="text-sm font-medium text-gray-700">
                    {viewMode === 'quarterly' ? `Q${histData.quarter}/${histData.year}` : `${label.split('/')[0]}/${histData.year.toString().slice(-2)}`}:
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {formatCurrency(histData.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-3 border-t border-gray-200 space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Média Histórica:</span>
              <span className="font-semibold text-gray-900">
                {formatCurrency(currentData.projectionDetails.averageHistorical)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">Variação vs Média:</span>
              <div className={`flex items-center gap-1 font-bold text-sm ${
                Math.abs(currentData.projectionDetails.variationFromAverage) < 5 ? 'text-gray-600' :
                currentData.projectionDetails.variationFromAverage > 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {currentData.projectionDetails.variationFromAverage > 5 && <TrendingUp className="w-4 h-4" />}
                {currentData.projectionDetails.variationFromAverage < -5 && <TrendingDown className="w-4 h-4" />}
                {Math.abs(currentData.projectionDetails.variationFromAverage) <= 5 && <Minus className="w-4 h-4" />}
                <span>
                  {currentData.projectionDetails.variationFromAverage > 0 ? '+' : ''}
                  {currentData.projectionDetails.variationFromAverage.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-800 border border-blue-200">
            <strong>Método:</strong> Média ponderada dos últimos {currentData.projectionDetails.historicalData.length} anos
            (anos recentes têm maior peso)
          </div>
        </div>
      ) : (
        <>
          <div className="space-y-2 mb-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total:</span>
              <span className="font-bold text-gray-900">
                {formatCurrency(currentTotal)}
              </span>
            </div>
            {projects > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Projetos:</span>
                <span className="text-sm font-medium text-green-600">
                  {formatCurrency(projects)}
                </span>
              </div>
            )}
            {contracts > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Contratos:</span>
                <span className="text-sm font-medium text-blue-600">
                  {formatCurrency(contracts)}
                </span>
              </div>
            )}
          </div>

          {yoyData && (
            <div className="pt-3 border-t border-gray-200">
              <div className="text-xs text-gray-500 mb-2">Comparação Anual</div>
              <div className="space-y-1.5">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">{yoyData.previousPeriod}:</span>
                  <span className="font-medium">{formatCurrency(yoyData.previousValue)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">Variação:</span>
                  <div className={`flex items-center gap-1 font-semibold text-sm ${
                    yoyData.isNeutral ? 'text-gray-600' :
                    yoyData.isPositive ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {yoyData.isPositive && !yoyData.isNeutral && '↑'}
                    {!yoyData.isPositive && !yoyData.isNeutral && '↓'}
                    {yoyData.isNeutral && '→'}
                    <span>{Math.abs(yoyData.percentageChange).toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!yoyData && (
            <div className="pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-500 italic">
                Dados do ano anterior não disponíveis
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const MONTHS = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Marco' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' }
];

export default function ReportsView() {
  const queryClient = useQueryClient();
  const [showDuplicateManager, setShowDuplicateManager] = useState(false);
  const [dataSource, setDataSource] = useState<DataSource>('combined');
  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');
  const [selectedMonth, setSelectedMonth] = useState<number | 'all'>('all');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editDate, setEditDate] = useState<string>('');
  const [viewMode, setViewMode] = useState<'monthly' | 'quarterly'>('monthly');
  const [visibleMetrics, setVisibleMetrics] = useState<Set<string>>(new Set());
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    item: CombinedItem | null;
  }>({ show: false, item: null });
  const [editingProject, setEditingProject] = useState<{
    show: boolean;
    id: string | null;
    name: string;
    value: string;
    plates: string;
    panelPower: string;
    inverter1Power: string;
    inverter1Qty: string;
    inverter2Power: string;
    inverter2Qty: string;
    saleDate: string;
    contact: string;
  }>({
    show: false,
    id: null,
    name: '',
    value: '',
    plates: '',
    panelPower: '',
    inverter1Power: '',
    inverter1Qty: '',
    inverter2Power: '',
    inverter2Qty: '',
    saleDate: '',
    contact: ''
  });

  const { data: projects, isLoading: projectsLoading } = useQuery(
    ['projects'],
    async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('sale_date', { ascending: false });

      if (error) throw error;
      return data as Project[];
    }
  );

  const { data: contractsData, isLoading: contractsLoading } = useQuery(
    ['contracts-reports'],
    async () => {
      const { data: financialData, error: financialError } = await supabase
        .from('financial_terms')
        .select(`
          id,
          total_amount,
          created_at,
          customer_id,
          customers!inner(
            id,
            full_name,
            cpf,
            phone
          )
        `)
        .order('created_at', { ascending: false });

      if (financialError) throw financialError;

      const { data: technicalData, error: technicalError } = await supabase
        .from('technical_configs')
        .select('*');

      if (technicalError) throw technicalError;

      const technicalMap = new Map();
      technicalData?.forEach(tech => {
        technicalMap.set(tech.customer_id, tech);
      });

      return financialData?.map(f => ({
        ...f,
        technical: technicalMap.get(f.customers.id)
      }));
    }
  );

  const updateProjectDate = useMutation(
    async ({ id, date }: { id: string; date: string | null }) => {
      const year = date ? new Date(date).getFullYear() : null;
      const { error } = await supabase
        .from('projects')
        .update({ sale_date: date, sale_year: year })
        .eq('id', id);
      if (error) throw error;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['projects']);
        setEditingProjectId(null);
      }
    }
  );

  const updateProject = useMutation(
    async (data: {
      id: string;
      name: string;
      value: number | null;
      plates: number | null;
      panelPower: number | null;
      inverter1Power: number | null;
      inverter1Qty: number | null;
      inverter2Power: number | null;
      inverter2Qty: number | null;
      saleDate: string | null;
      contact: string | null;
    }) => {
      const year = data.saleDate ? new Date(data.saleDate).getFullYear() : null;
      const { error } = await supabase
        .from('projects')
        .update({
          name: data.name,
          value: data.value,
          plates: data.plates,
          panel_power: data.panelPower,
          inverter1_power: data.inverter1Power,
          inverter1_quantity: data.inverter1Qty,
          inverter2_power: data.inverter2Power,
          inverter2_quantity: data.inverter2Qty,
          sale_date: data.saleDate,
          sale_year: year,
          contact: data.contact,
          updated_at: new Date().toISOString()
        })
        .eq('id', data.id);
      if (error) throw error;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['projects']);
        setEditingProject({
          show: false,
          id: null,
          name: '',
          value: '',
          plates: '',
          panelPower: '',
          inverter1Power: '',
          inverter1Qty: '',
          inverter2Power: '',
          inverter2Qty: '',
          saleDate: '',
          contact: ''
        });
      }
    }
  );

  const deleteProject = useMutation(
    async (id: string) => {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['projects']);
        setDeleteConfirmation({ show: false, item: null });
      },
      onError: (error: any) => {
        console.error('Erro ao excluir projeto:', error);
        alert(`Erro ao excluir projeto: ${error.message || 'Erro desconhecido'}`);
      }
    }
  );

  const deleteContract = useMutation(
    async (customerId: string) => {
      const { error: financialError } = await supabase
        .from('financial_terms')
        .delete()
        .eq('customer_id', customerId);

      if (financialError) throw financialError;

      const { error: technicalError } = await supabase
        .from('technical_configs')
        .delete()
        .eq('customer_id', customerId);

      if (technicalError) throw technicalError;

      const { error: installationError } = await supabase
        .from('installation_locations')
        .delete()
        .eq('customer_id', customerId);

      if (installationError) throw installationError;

      const { error: specsError } = await supabase
        .from('installation_specs')
        .delete()
        .eq('customer_id', customerId);

      if (specsError) throw specsError;

      const { error: timelineError } = await supabase
        .from('project_timelines')
        .delete()
        .eq('customer_id', customerId);

      if (timelineError) throw timelineError;

      const { error: customerError } = await supabase
        .from('customers')
        .delete()
        .eq('id', customerId);

      if (customerError) throw customerError;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['contracts-reports']);
        setDeleteConfirmation({ show: false, item: null });
      },
      onError: (error: any) => {
        console.error('Erro ao excluir contrato:', error);
        alert(`Erro ao excluir contrato: ${error.message || 'Erro desconhecido'}`);
      }
    }
  );

  const handleSaveDate = (id: string) => {
    updateProjectDate.mutate({ id, date: editDate || null });
  };

  const handleEditProject = (item: CombinedItem) => {
    if (item.type !== 'project') return;

    const project = projects?.find(p => p.id === item.id);
    if (!project) return;

    setEditingProject({
      show: true,
      id: project.id,
      name: project.name,
      value: project.value?.toString() || '',
      plates: project.plates?.toString() || '',
      panelPower: (project as any).panel_power?.toString() || '',
      inverter1Power: (project as any).inverter1_power?.toString() || '',
      inverter1Qty: (project as any).inverter1_quantity?.toString() || '',
      inverter2Power: (project as any).inverter2_power?.toString() || '',
      inverter2Qty: (project as any).inverter2_quantity?.toString() || '',
      saleDate: project.sale_date ? format(new Date(project.sale_date), 'yyyy-MM-dd') : '',
      contact: project.contact || ''
    });
  };

  const handleSaveProject = () => {
    if (!editingProject.id) return;

    updateProject.mutate({
      id: editingProject.id,
      name: editingProject.name,
      value: editingProject.value ? parseFloat(editingProject.value) : null,
      plates: editingProject.plates ? parseInt(editingProject.plates) : null,
      panelPower: editingProject.panelPower ? parseFloat(editingProject.panelPower) : null,
      inverter1Power: editingProject.inverter1Power ? parseFloat(editingProject.inverter1Power) : null,
      inverter1Qty: editingProject.inverter1Qty ? parseInt(editingProject.inverter1Qty) : null,
      inverter2Power: editingProject.inverter2Power ? parseFloat(editingProject.inverter2Power) : null,
      inverter2Qty: editingProject.inverter2Qty ? parseInt(editingProject.inverter2Qty) : null,
      saleDate: editingProject.saleDate || null,
      contact: editingProject.contact || null
    });
  };

  const handleDeleteClick = (item: CombinedItem) => {
    setDeleteConfirmation({ show: true, item });
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirmation.item) {
      console.error('Nenhum item selecionado para excluir');
      return;
    }

    console.log('Tentando excluir:', deleteConfirmation.item);

    if (deleteConfirmation.item.type === 'project') {
      deleteProject.mutate(deleteConfirmation.item.id);
    } else {
      const contractItem = contractsData?.find(c => c.id === deleteConfirmation.item?.id);
      if (contractItem) {
        console.log('ID do cliente encontrado:', contractItem.customers.id);
        deleteContract.mutate(contractItem.customers.id);
      } else {
        console.error('Contrato não encontrado');
        alert('Erro: Contrato não encontrado. Por favor, recarregue a página e tente novamente.');
      }
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const toggleMetric = (metric: string) => {
    const newSet = new Set(visibleMetrics);
    if (newSet.has(metric)) {
      newSet.delete(metric);
    } else {
      newSet.add(metric);
    }
    setVisibleMetrics(newSet);
  };

  const availableYears = useMemo(() => {
    const years = new Set<number>();

    projects?.forEach(p => {
      if (p.sale_date) {
        years.add(new Date(p.sale_date).getFullYear());
      } else if (p.sale_year) {
        years.add(p.sale_year);
      }
    });

    contractsData?.forEach(c => {
      years.add(new Date(c.created_at).getFullYear());
    });

    return Array.from(years).sort((a, b) => b - a);
  }, [projects, contractsData]);

  const combinedData = useMemo((): CombinedItem[] => {
    const items: CombinedItem[] = [];

    if (dataSource === 'combined' || dataSource === 'projects') {
      projects?.forEach(p => {
        if (p.value !== null) {
          const date = p.sale_date ? new Date(p.sale_date) : (p.sale_year ? new Date(p.sale_year, 0, 1) : new Date(p.created_at));

          const solarPower = p.plates && p.panel_power
            ? (p.plates * p.panel_power) / 1000
            : undefined;

          const inverterPower = (p.inverter1_power && p.inverter1_quantity)
            ? (p.inverter1_power * p.inverter1_quantity) + ((p.inverter2_power || 0) * (p.inverter2_quantity || 0))
            : undefined;

          items.push({
            id: p.id,
            type: 'project',
            name: p.name,
            value: p.value,
            date,
            plates: p.plates,
            contact: p.contact,
            modules: p.plates,
            modulePower: p.panel_power,
            solarPower,
            inverter1Qty: p.inverter1_quantity,
            inverter1Power: p.inverter1_power,
            inverter2Qty: p.inverter2_quantity,
            inverter2Power: p.inverter2_power,
            inverterPower
          });
        }
      });
    }

    if (dataSource === 'combined' || dataSource === 'contracts') {
      contractsData?.forEach(c => {
        const tech = c.technical;
        items.push({
          id: c.id,
          type: 'contract',
          name: c.customers.full_name,
          value: c.total_amount,
          date: new Date(c.created_at),
          plates: null,
          contact: c.customers.phone,
          modules: tech?.solar_modules_quantity,
          modulePower: tech?.solar_modules_power,
          solarPower: tech ? (tech.solar_modules_power * tech.solar_modules_quantity) / 1000 : undefined,
          inverter1Qty: tech?.inverter1_quantity,
          inverter1Power: tech?.inverter1_power,
          inverter2Qty: tech?.inverter2_quantity,
          inverter2Power: tech?.inverter2_power,
          inverterPower: tech ? (tech.inverter1_power * tech.inverter1_quantity) + ((tech.inverter2_power || 0) * (tech.inverter2_quantity || 0)) : undefined
        });
      });
    }

    return items;
  }, [projects, contractsData, dataSource]);

  const filteredData = useMemo(() => {
    let filtered = [...combinedData];

    if (selectedYear !== 'all') {
      filtered = filtered.filter(item => item.date.getFullYear() === selectedYear);
    }

    if (selectedMonth !== 'all') {
      filtered = filtered.filter(item => item.date.getMonth() + 1 === selectedMonth);
    }

    filtered.sort((a, b) => {
      let compareA: any;
      let compareB: any;

      switch (sortField) {
        case 'name':
          compareA = a.name.toLowerCase();
          compareB = b.name.toLowerCase();
          break;
        case 'value':
          compareA = a.value;
          compareB = b.value;
          break;
        case 'date':
          compareA = a.date.getTime();
          compareB = b.date.getTime();
          break;
        case 'plates':
          compareA = a.plates ?? a.modules ?? 0;
          compareB = b.plates ?? b.modules ?? 0;
          break;
        default:
          return 0;
      }

      if (typeof compareA === 'string' && typeof compareB === 'string') {
        return sortOrder === 'asc' ? compareA.localeCompare(compareB) : compareB.localeCompare(compareA);
      }
      return sortOrder === 'asc' ? compareA - compareB : compareB - compareA;
    });

    return filtered;
  }, [combinedData, selectedYear, selectedMonth, sortField, sortOrder]);

  const stats = useMemo(() => {
    const projectItems = filteredData.filter(i => i.type === 'project');
    const contractItems = filteredData.filter(i => i.type === 'contract');

    const totalInverter1Qty = filteredData.reduce((acc, i) => acc + (i.inverter1Qty || 0), 0);
    const totalInverter2Qty = filteredData.reduce((acc, i) => acc + (i.inverter2Qty || 0), 0);
    const totalInverterQty = totalInverter1Qty + totalInverter2Qty;

    const totalInverterPower = filteredData.reduce((acc, i) => acc + (i.inverterPower || 0), 0);
    const totalSolarPower = filteredData.reduce((acc, i) => acc + (i.solarPower || 0), 0);

    return {
      totalValue: filteredData.reduce((acc, i) => acc + i.value, 0),
      totalCount: filteredData.length,
      projectCount: projectItems.length,
      contractCount: contractItems.length,
      projectValue: projectItems.reduce((acc, i) => acc + i.value, 0),
      contractValue: contractItems.reduce((acc, i) => acc + i.value, 0),
      totalPlates: projectItems.reduce((acc, i) => acc + (i.plates || 0), 0),
      totalModules: filteredData.reduce((acc, i) => acc + (i.modules || 0), 0),
      avgValue: filteredData.length ? filteredData.reduce((acc, i) => acc + i.value, 0) / filteredData.length : 0,
      totalInverterQty,
      totalInverter1Qty,
      totalInverter2Qty,
      totalInverterPower,
      totalSolarPower
    };
  }, [filteredData]);

  const chartData = useMemo(() => {
    const dataToUse = selectedYear === 'all' && selectedMonth === 'all' ? combinedData : filteredData;

    if (viewMode === 'quarterly') {
      const byQuarter = new Map<string, {
        period: string;
        projects: number;
        contracts: number;
        total: number;
        year: number;
        quarter: number;
        sortKey: string;
        count: number;
        avgTicket: number;
        plates: number;
        kwp: number;
        projection?: number;
        isProjection?: boolean;
        projectionDetails?: ProjectionData;
      }>();

      dataToUse.forEach(item => {
        const year = item.date.getFullYear();
        const month = item.date.getMonth();
        const quarter = Math.floor(month / 3) + 1;
        const quarterKey = `Q${quarter}/${year.toString().slice(-2)}`;
        const sortKey = `${year}-Q${quarter}`;

        if (!byQuarter.has(sortKey)) {
          byQuarter.set(sortKey, {
            period: quarterKey,
            projects: 0,
            contracts: 0,
            total: 0,
            year,
            quarter,
            sortKey,
            count: 0,
            avgTicket: 0,
            plates: 0,
            kwp: 0
          });
        }

        const data = byQuarter.get(sortKey)!;
        if (item.type === 'project') {
          data.projects += item.value;
        } else {
          data.contracts += item.value;
        }
        data.total += item.value;
        data.count += 1;
        data.plates += (item.plates || item.modules || 0);

        // Calcular kWp
        let itemKwp = 0;
        if (item.type === 'contract' && item.solarPower) {
          itemKwp = item.solarPower;
        } else if (item.plates && item.plates > 0) {
          // Estimar kWp para projetos (assumindo 550W por placa)
          itemKwp = (item.plates * 550) / 1000;
        }
        data.kwp += itemKwp;
      });

      // Calcular ticket médio
      byQuarter.forEach(data => {
        data.avgTicket = data.count > 0 ? data.total / data.count : 0;
      });

      const sortedData = Array.from(byQuarter.values()).sort((a, b) => {
        return a.sortKey.localeCompare(b.sortKey);
      });

      if (selectedYear === 'all' && selectedMonth === 'all' && sortedData.length > 0) {
        const today = new Date();
        const currentMonth = today.getMonth();
        const currentQuarter = Math.floor(currentMonth / 3) + 1;
        const currentYear = today.getFullYear();

        for (let i = 1; i <= 2; i++) {
          const futureQuarter = ((currentQuarter + i - 1) % 4) + 1;
          const futureYear = currentYear + Math.floor((currentQuarter + i - 1) / 4);
          const quarterKey = `Q${futureQuarter}/${futureYear.toString().slice(-2)}`;
          const sortKey = `${futureYear}-Q${futureQuarter}`;

          const salesData = combinedData.map(item => ({
            date: item.date,
            value: item.value
          }));

          const projectionDetails = ProjectionService.calculateQuarterlyProjection(
            salesData,
            futureQuarter,
            futureYear
          );

          if (projectionDetails) {
            sortedData.push({
              period: quarterKey,
              projects: 0,
              contracts: 0,
              total: 0,
              year: futureYear,
              quarter: futureQuarter,
              sortKey,
              projection: projectionDetails.projectedValue,
              isProjection: true,
              projectionDetails
            });
          }
        }
      }

      return sortedData;
    }

    const byMonth = new Map<string, {
      month: string;
      projects: number;
      contracts: number;
      total: number;
      year: number;
      monthNum: number;
      sortKey: string;
      count: number;
      avgTicket: number;
      plates: number;
      kwp: number;
      projection?: number;
      isProjection?: boolean;
      projectionDetails?: ProjectionData;
    }>();

    dataToUse.forEach(item => {
      const year = item.date.getFullYear();
      const monthNum = item.date.getMonth();
      const monthKey = format(item.date, 'MMM/yy', { locale: ptBR });
      const sortKey = `${year}-${String(monthNum).padStart(2, '0')}`;

      if (!byMonth.has(sortKey)) {
        byMonth.set(sortKey, {
          month: monthKey,
          projects: 0,
          contracts: 0,
          total: 0,
          year,
          monthNum,
          sortKey,
          count: 0,
          avgTicket: 0,
          plates: 0,
          kwp: 0
        });
      }

      const data = byMonth.get(sortKey)!;
      if (item.type === 'project') {
        data.projects += item.value;
      } else {
        data.contracts += item.value;
      }
      data.total += item.value;
      data.count += 1;
      data.plates += (item.plates || item.modules || 0);

      // Calcular kWp
      let itemKwp = 0;
      if (item.type === 'contract' && item.solarPower) {
        itemKwp = item.solarPower;
      } else if (item.plates && item.plates > 0) {
        // Estimar kWp para projetos (assumindo 550W por placa)
        itemKwp = (item.plates * 550) / 1000;
      }
      data.kwp += itemKwp;
    });

    // Calcular ticket médio
    byMonth.forEach(data => {
      data.avgTicket = data.count > 0 ? data.total / data.count : 0;
    });

    const sortedData = Array.from(byMonth.values()).sort((a, b) => {
      return a.sortKey.localeCompare(b.sortKey);
    });

    if (selectedYear === 'all' && selectedMonth === 'all' && sortedData.length > 0) {
      const today = new Date();
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();

      for (let i = 1; i <= 3; i++) {
        const futureDate = new Date(currentYear, currentMonth + i, 1);
        const futureMonth = futureDate.getMonth();
        const futureYear = futureDate.getFullYear();
        const monthKey = format(futureDate, 'MMM/yy', { locale: ptBR });
        const sortKey = `${futureYear}-${String(futureMonth).padStart(2, '0')}`;

        const salesData = combinedData.map(item => ({
          date: item.date,
          value: item.value
        }));

        const projectionDetails = ProjectionService.calculateMonthlyProjection(
          salesData,
          futureMonth,
          futureYear
        );

        if (projectionDetails) {
          sortedData.push({
            month: monthKey,
            projects: 0,
            contracts: 0,
            total: 0,
            year: futureYear,
            monthNum: futureMonth,
            sortKey,
            projection: projectionDetails.projectedValue,
            isProjection: true,
            projectionDetails
          });
        }
      }
    }

    return sortedData;
  }, [combinedData, filteredData, selectedYear, selectedMonth, viewMode]);

  const exportCSV = () => {
    if (!filteredData.length) return;

    const headers = [
      'Tipo',
      'Data',
      'Nome',
      'Valor',
      'Placas/Modulos',
      'Inversores Qtd',
      'Inversores Potencia (kW)',
      'Paineis Potencia (kW)',
      'Potencia Painel (W)',
      'Contato'
    ];

    const rows = filteredData.map(item => [
      item.type === 'project' ? 'Projeto' : 'Contrato',
      format(item.date, 'dd/MM/yyyy'),
      item.name,
      item.value.toFixed(2),
      (item.plates || item.modules || '').toString(),
      item.type === 'contract' ? ((item.inverter1Qty || 0) + (item.inverter2Qty || 0)).toString() : '',
      item.inverterPower ? item.inverterPower.toFixed(2) : '',
      item.solarPower ? item.solarPower.toFixed(2) : '',
      item.modulePower || '',
      item.contact || ''
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const yearLabel = selectedYear === 'all' ? 'todos' : selectedYear;
    const monthLabel = selectedMonth === 'all' ? '' : `-${MONTHS.find(m => m.value === selectedMonth)?.label}`;
    link.download = `relatorio-${dataSource}-${yearLabel}${monthLabel}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const SortHeader = ({ field, label, align = 'left' }: { field: SortField; label: string; align?: 'left' | 'right' }) => (
    <th
      onClick={() => handleSort(field)}
      className={`px-4 py-3 text-${align} text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors`}
    >
      <div className={`flex items-center gap-2 ${align === 'right' ? 'justify-end' : ''}`}>
        {label}
        {sortField === field && (
          <ArrowUpDown className={`w-4 h-4 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />
        )}
      </div>
    </th>
  );

  const isLoading = projectsLoading || contractsLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SalesComparisonChart />

      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Relatorios de Vendas</h2>

          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setDataSource('combined')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  dataSource === 'combined'
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Layers className="w-4 h-4 inline mr-1" />
                Combinado
              </button>
              <button
                onClick={() => setDataSource('projects')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  dataSource === 'projects'
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                Projetos
              </button>
              <button
                onClick={() => setDataSource('contracts')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  dataSource === 'contracts'
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                Contratos
              </button>
            </div>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos os anos</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>

            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos os meses</option>
              {MONTHS.map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>

            <button
              onClick={() => setShowDuplicateManager(true)}
              className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 text-sm"
            >
              <Copy className="w-4 h-4" />
              Duplicatas
            </button>

            <button
              onClick={exportCSV}
              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm"
            >
              <Download className="w-4 h-4" />
              CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-5 text-white">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium opacity-90">Valor Total</h3>
              <DollarSign className="w-5 h-5 opacity-80" />
            </div>
            <p className="text-2xl font-bold">
              R$ {stats.totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs opacity-80 mt-1">{stats.totalCount} registros</p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-5 text-white">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium opacity-90">Projetos</h3>
              <FileText className="w-5 h-5 opacity-80" />
            </div>
            <p className="text-2xl font-bold">
              R$ {stats.projectValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs opacity-80 mt-1">{stats.projectCount} projetos | {stats.totalPlates} placas</p>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-5 text-white">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium opacity-90">Contratos</h3>
              <Zap className="w-5 h-5 opacity-80" />
            </div>
            <p className="text-2xl font-bold">
              R$ {stats.contractValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs opacity-80 mt-1">{stats.contractCount} contratos | {stats.totalModules} modulos</p>
          </div>

          <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg p-5 text-white">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium opacity-90">Ticket Medio</h3>
              <TrendingUp className="w-5 h-5 opacity-80" />
            </div>
            <p className="text-2xl font-bold">
              R$ {stats.avgValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs opacity-80 mt-1">por venda</p>
          </div>
        </div>

        {(dataSource === 'combined' || dataSource === 'contracts' || dataSource === 'projects') && (stats.totalInverterQty > 0 || stats.totalSolarPower > 0) && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-6 mb-6 border-2 border-amber-200">
            <div className="flex items-center gap-3 mb-4">
              <Zap className="w-6 h-6 text-amber-600" />
              <h3 className="text-lg font-bold text-gray-800">Resumo Técnico de Equipamentos</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-5 shadow-sm border border-amber-100">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-orange-600" />
                  Inversores Vendidos
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Quantidade Total:</span>
                    <span className="text-xl font-bold text-gray-900">{stats.totalInverterQty}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-100 space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Inversor Principal:</span>
                      <span className="font-semibold text-gray-700">{stats.totalInverter1Qty} unidades</span>
                    </div>
                    {stats.totalInverter2Qty > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Inversor Secundário:</span>
                        <span className="font-semibold text-gray-700">{stats.totalInverter2Qty} unidades</span>
                      </div>
                    )}
                  </div>
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Potência Total:</span>
                      <span className="text-2xl font-bold text-orange-600">
                        {stats.totalInverterPower.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kW
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 text-right">
                      {(stats.totalInverterPower / 1000).toFixed(3)} MW
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-5 shadow-sm border border-amber-100">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-blue-600" />
                  Painéis Solares Vendidos
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Quantidade Total:</span>
                    <span className="text-xl font-bold text-gray-900">{stats.totalModules}</span>
                  </div>
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Potência Total:</span>
                      <span className="text-2xl font-bold text-blue-600">
                        {stats.totalSolarPower.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kW
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 text-right">
                      {(stats.totalSolarPower * 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} W | {(stats.totalSolarPower / 1000).toFixed(3)} MW
                    </div>
                  </div>
                  {stats.totalModules > 0 && (
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Potência Média/Painel:</span>
                        <span className="font-semibold text-gray-700">
                          {((stats.totalSolarPower * 1000) / stats.totalModules).toFixed(0)} W
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {chartData.length > 1 && (
          <div className="bg-gray-50 rounded-lg p-6 border mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Vendas por Período</h3>
              <div className="flex gap-1 bg-white p-1 rounded-lg border">
                <button
                  onClick={() => setViewMode('monthly')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'monthly'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Mensal
                </button>
                <button
                  onClick={() => setViewMode('quarterly')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'quarterly'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Trimestral
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Métricas Adicionais (linhas)</label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => toggleMetric('quantity')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                    visibleMetrics.has('quantity')
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {visibleMetrics.has('quantity') ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  <ShoppingCart className="w-4 h-4" />
                  <span>Quantidade</span>
                </button>

                <button
                  onClick={() => toggleMetric('avgTicket')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                    visibleMetrics.has('avgTicket')
                      ? 'border-amber-500 bg-amber-50 text-amber-700'
                      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {visibleMetrics.has('avgTicket') ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  <TrendingUp className="w-4 h-4" />
                  <span>Ticket Médio</span>
                </button>

                <button
                  onClick={() => toggleMetric('plates')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                    visibleMetrics.has('plates')
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {visibleMetrics.has('plates') ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  <Grid3x3 className="w-4 h-4" />
                  <span>Placas</span>
                </button>

                <button
                  onClick={() => toggleMetric('kwp')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                    visibleMetrics.has('kwp')
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {visibleMetrics.has('kwp') ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  <Zap className="w-4 h-4" />
                  <span>kWp</span>
                </button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey={viewMode === 'quarterly' ? 'period' : 'month'}
                  tick={{ fill: '#666', fontSize: 12 }}
                  tickFormatter={(value, index) => {
                    const item = chartData[index];
                    return item?.isProjection ? `${value}*` : value;
                  }}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fill: '#666', fontSize: 12 }}
                  tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: '#666', fontSize: 12 }}
                  tickFormatter={(v) => v.toFixed(0)}
                />
                <Tooltip
                  content={<CustomTooltip viewMode={viewMode} allData={combinedData} />}
                  cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                  animationDuration={150}
                />
                <Legend
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="square"
                />
                {dataSource === 'combined' ? (
                  <>
                    <Bar
                      dataKey="projects"
                      fill="#10b981"
                      name="Projetos (R$)"
                      stackId="a"
                      radius={[0, 0, 0, 0]}
                      animationDuration={500}
                      yAxisId="left"
                    />
                    <Bar
                      dataKey="contracts"
                      fill="#3b82f6"
                      name="Contratos (R$)"
                      stackId="a"
                      radius={[4, 4, 0, 0]}
                      animationDuration={500}
                      yAxisId="left"
                    />
                  </>
                ) : (
                  <Bar
                    dataKey="total"
                    fill={dataSource === 'projects' ? '#10b981' : '#3b82f6'}
                    name="Faturamento (R$)"
                    radius={[4, 4, 0, 0]}
                    animationDuration={500}
                    yAxisId="left"
                  />
                )}

                {visibleMetrics.has('quantity') && (
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#10b981"
                    strokeWidth={2}
                    name="Quantidade"
                    dot={{ fill: '#10b981', r: 4 }}
                    activeDot={{ r: 6 }}
                    yAxisId="right"
                    animationDuration={500}
                  />
                )}

                {visibleMetrics.has('avgTicket') && (
                  <Line
                    type="monotone"
                    dataKey="avgTicket"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    name="Ticket Médio (R$)"
                    dot={{ fill: '#f59e0b', r: 4 }}
                    activeDot={{ r: 6 }}
                    yAxisId="left"
                    animationDuration={500}
                  />
                )}

                {visibleMetrics.has('plates') && (
                  <Line
                    type="monotone"
                    dataKey="plates"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    name="Placas"
                    dot={{ fill: '#8b5cf6', r: 4 }}
                    activeDot={{ r: 6 }}
                    yAxisId="right"
                    animationDuration={500}
                  />
                )}

                {visibleMetrics.has('kwp') && (
                  <Line
                    type="monotone"
                    dataKey="kwp"
                    stroke="#ef4444"
                    strokeWidth={2}
                    name="kWp"
                    dot={{ fill: '#ef4444', r: 4 }}
                    activeDot={{ r: 6 }}
                    yAxisId="right"
                    animationDuration={500}
                  />
                )}

                {viewMode === 'monthly' && (
                  <Line
                    type="monotone"
                    dataKey="projection"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="Projeção (R$)"
                    dot={{ fill: '#f59e0b', r: 4 }}
                    activeDot={{ r: 6 }}
                    connectNulls={false}
                    yAxisId="left"
                    animationDuration={500}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
            {viewMode === 'monthly' && selectedYear === 'all' && selectedMonth === 'all' && (
              <p className="text-xs text-gray-500 mt-3">
                * Meses com projeção baseada em histórico dos mesmos meses em anos anteriores
              </p>
            )}
            {viewMode === 'quarterly' && (
              <p className="text-xs text-gray-500 mt-3">
                Visualização por trimestre: Q1 (Jan-Mar), Q2 (Abr-Jun), Q3 (Jul-Set), Q4 (Out-Dez)
              </p>
            )}
          </div>
        )}

        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-4">
            <FileText className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="font-semibold text-gray-800">
                {dataSource === 'combined' ? 'Projetos e Contratos' : dataSource === 'projects' ? 'Projetos' : 'Contratos'}
              </h3>
              <p className="text-sm text-gray-600">
                {filteredData.length} registros
                {selectedYear !== 'all' && ` em ${selectedYear}`}
                {selectedMonth !== 'all' && ` - ${MONTHS.find(m => m.value === selectedMonth)?.label}`}
              </p>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <SortHeader field="date" label="Data" />
                <SortHeader field="name" label="Nome" />
                <SortHeader field="value" label="Valor" align="right" />
                <SortHeader field="plates" label="Placas/Modulos" align="right" />
                {(dataSource === 'combined' || dataSource === 'contracts') && (
                  <>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Inversores
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pot. Inv. (kW)
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pot. Painéis (kW)
                    </th>
                  </>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contato
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.map((item) => (
                <tr key={`${item.type}-${item.id}`} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      item.type === 'project'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {item.type === 'project' ? 'Projeto' : 'Contrato'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.type === 'project' && editingProjectId === item.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                          className="px-2 py-1 border rounded text-sm w-36"
                        />
                        <button
                          onClick={() => handleSaveDate(item.id)}
                          className="text-green-600 hover:text-green-800"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingProjectId(null)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>{format(item.date, 'dd/MM/yyyy')}</span>
                        {item.type === 'project' && (
                          <button
                            onClick={() => {
                              setEditingProjectId(item.id);
                              setEditDate(format(item.date, 'yyyy-MM-dd'));
                            }}
                            className="text-gray-400 hover:text-blue-600"
                          >
                            <Edit3 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900">
                    {item.name}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                    R$ {item.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {item.plates || item.modules || '-'}
                  </td>
                  {(dataSource === 'combined' || dataSource === 'contracts') && (
                    <>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                        {(item.inverter1Qty || item.inverter2Qty) ? (
                          <div className="flex flex-col items-end">
                            <span className="font-medium">{(item.inverter1Qty || 0) + (item.inverter2Qty || 0)}</span>
                            <span className="text-xs text-gray-500">
                              {item.inverter1Qty && `${item.inverter1Qty}x${item.inverter1Power}kW`}
                              {item.inverter2Qty && ` + ${item.inverter2Qty}x${item.inverter2Power}kW`}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        {item.inverterPower ? (
                          <span className="text-orange-600">
                            {item.inverterPower.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        {item.solarPower ? (
                          <div className="flex flex-col items-end">
                            <span className="text-blue-600">
                              {item.solarPower.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                            <span className="text-xs text-gray-500">
                              {item.modules}x{item.modulePower}W
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.contact || <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center gap-2">
                      {item.type === 'project' && (
                        <button
                          onClick={() => handleEditProject(item)}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteClick(item)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-lg transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredData.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nenhum registro encontrado com os filtros selecionados.</p>
          </div>
        )}
      </div>

      {editingProject.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full my-8">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Editar Projeto</h3>
              <button
                onClick={() => setEditingProject({ ...editingProject, show: false })}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={updateProject.isLoading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome do Cliente
                </label>
                <input
                  type="text"
                  value={editingProject.name}
                  onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nome do cliente"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Data da Venda
                  </label>
                  <input
                    type="date"
                    value={editingProject.saleDate}
                    onChange={(e) => setEditingProject({ ...editingProject, saleDate: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valor Total (R$)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingProject.value}
                    onChange={(e) => setEditingProject({ ...editingProject, value: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantidade de Módulos
                  </label>
                  <input
                    type="number"
                    value={editingProject.plates}
                    onChange={(e) => setEditingProject({ ...editingProject, plates: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: 10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Potência do Módulo (W)
                  </label>
                  <input
                    type="number"
                    value={editingProject.panelPower}
                    onChange={(e) => setEditingProject({ ...editingProject, panelPower: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: 550"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Inversor 1</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantidade
                    </label>
                    <input
                      type="number"
                      value={editingProject.inverter1Qty}
                      onChange={(e) => setEditingProject({ ...editingProject, inverter1Qty: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: 1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Potência (kW)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingProject.inverter1Power}
                      onChange={(e) => setEditingProject({ ...editingProject, inverter1Power: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: 5.5"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Inversor 2 (Opcional)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantidade
                    </label>
                    <input
                      type="number"
                      value={editingProject.inverter2Qty}
                      onChange={(e) => setEditingProject({ ...editingProject, inverter2Qty: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: 1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Potência (kW)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingProject.inverter2Power}
                      onChange={(e) => setEditingProject({ ...editingProject, inverter2Power: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: 3.0"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contato
                </label>
                <input
                  type="text"
                  value={editingProject.contact}
                  onChange={(e) => setEditingProject({ ...editingProject, contact: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Telefone ou email"
                />
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setEditingProject({ ...editingProject, show: false })}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium transition-colors"
                disabled={updateProject.isLoading}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveProject}
                disabled={updateProject.isLoading || !editingProject.name}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {updateProject.isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Salvar Alterações
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmation.show && deleteConfirmation.item && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Confirmar Exclusão</h3>
                <p className="text-sm text-gray-600">Esta ação não pode ser desfeita</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6 border-2 border-gray-200">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    deleteConfirmation.item.type === 'project'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {deleteConfirmation.item.type === 'project' ? 'Projeto' : 'Contrato'}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{deleteConfirmation.item.name}</p>
                  <p className="text-lg font-bold text-red-600 mt-1">
                    R$ {deleteConfirmation.item.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="text-xs text-gray-600 pt-2 border-t border-gray-200">
                  <p>Data: {format(deleteConfirmation.item.date, 'dd/MM/yyyy')}</p>
                  {(deleteConfirmation.item.plates || deleteConfirmation.item.modules) && (
                    <p>Placas/Módulos: {deleteConfirmation.item.plates || deleteConfirmation.item.modules}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 mb-6">
              <p className="text-sm text-amber-800">
                <strong>Atenção:</strong> {deleteConfirmation.item.type === 'project'
                  ? 'O projeto será excluído permanentemente do sistema.'
                  : 'Todos os dados do contrato (cliente, configurações técnicas, localização, etc.) serão excluídos permanentemente.'}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmation({ show: false, item: null })}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium transition-colors"
                disabled={deleteProject.isLoading || deleteContract.isLoading}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteProject.isLoading || deleteContract.isLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {(deleteProject.isLoading || deleteContract.isLoading) ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Confirmar Exclusão
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen p-4">
            <div className="bg-white rounded-lg shadow-2xl">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Gerenciador de Duplicatas</h2>
                <button
                  onClick={() => setShowDuplicateManager(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <DuplicateManager />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
